import { useMemo, useState } from 'react';
import { color } from '../styles/theme';
import { fieldErrorsFrom } from '../lib/fieldErrors';
import { fullDate } from '../lib/format';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useCreateMeeting, useMeetingPeople, useMeetings } from '../api/meetings';
import { Button, ErrorNote, Field, Modal, inputStyle, selectStyle } from '../components/ui';
import { sourceFromRow, type ContinueSource } from './MeetingBits';

/**
 * New meeting: a title, when, who — and the one choice that makes weeks
 * connect: start fresh, or continue a previous meeting and take on what it
 * left open.
 */

const pad2 = (n: number) => String(n).padStart(2, '0');

/** A local date and time, as the two native inputs hold them. */
function localParts(iso: string | null): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  return {
    date: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
    time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
  };
}

/** A week on from a meeting, at the same time — the usual next one. */
function weekAfter(iso: string | null): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  d.setDate(d.getDate() + 7);
  return localParts(d.toISOString());
}

function carryLine(c: ContinueSource['carry']): string {
  const bits: string[] = [];
  bits.push(`${c.actions} open action item${c.actions === 1 ? '' : 's'}`);
  bits.push(`${c.takeaways} takeaway${c.takeaways === 1 ? '' : 's'}`);
  bits.push(`${c.questions} open question${c.questions === 1 ? '' : 's'}`);
  return bits.join(' · ');
}

export function NewMeeting({
  continuesFrom,
  onClose,
  onCreated,
}: {
  /** Prefilled from "Continue this meeting"; null from the list's New meeting. */
  continuesFrom: ContinueSource | null;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { admin } = useAuth();
  const people = useMeetingPeople();
  // The candidates to continue: the newest fifty, Done ones offered first.
  const recent = useMeetings({ pageSize: 50 });
  const create = useCreateMeeting();

  const candidates = useMemo<ContinueSource[]>(() => {
    const rows = (recent.data?.rows ?? []).map(sourceFromRow);
    const done = rows.filter((r) => r.phase === 'done');
    const rest = rows.filter((r) => r.phase !== 'done');
    const all = [...done, ...rest];
    if (continuesFrom && !all.some((r) => r.id === continuesFrom.id)) all.unshift(continuesFrom);
    return all;
  }, [recent.data, continuesFrom]);

  const [mode, setMode] = useState<'fresh' | 'continue'>(continuesFrom ? 'continue' : 'fresh');
  const [sourceId, setSourceId] = useState<string>(continuesFrom?.id ?? '');
  const source = useMemo(() => {
    const id = sourceId || candidates[0]?.id;
    return candidates.find((c) => c.id === id) ?? null;
  }, [sourceId, candidates]);

  const [title, setTitle] = useState(continuesFrom?.title ?? '');
  const startWhen = continuesFrom ? weekAfter(continuesFrom.scheduledAt) : { date: '', time: '' };
  const [date, setDate] = useState(startWhen.date);
  const [time, setTime] = useState(startWhen.time);
  const [attendees, setAttendees] = useState<Set<string>>(
    () => new Set(continuesFrom ? continuesFrom.attendeeIds : admin ? [admin.id] : []),
  );
  const [touched, setTouched] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  /** Picking a meeting to continue fills the title, the time and the room, unless the person already typed. */
  function pickSource(id: string) {
    setSourceId(id);
    const c = candidates.find((x) => x.id === id);
    if (!c || touched) return;
    setTitle(c.title);
    const when = weekAfter(c.scheduledAt);
    setDate(when.date);
    setTime(when.time);
    setAttendees(new Set(c.attendeeIds));
  }

  function toggle(id: string) {
    setAttendees((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  const scheduledAt = date ? new Date(`${date}T${time || '09:00'}`).toISOString() : null;
  const continuing = mode === 'continue' && source !== null;

  async function submit() {
    setErrors({});
    try {
      const d = await create.mutateAsync({
        title: title.trim(),
        scheduledAt,
        attendeeIds: [...attendees],
        continuesFromId: continuing ? source.id : null,
      });
      onCreated(d.meeting.id);
    } catch (e) {
      if (e instanceof ApiError) setErrors(fieldErrorsFrom(e.details));
    }
  }

  const ready = title.trim().length > 0 && !create.isPending;

  return (
    <Modal
      label="New meeting"
      width={640}
      onClose={onClose}
      header={<span style={{ fontSize: 17, fontWeight: 700, color: color.navy }}>New meeting</span>}
      footer={
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
          {create.error && !(create.error instanceof ApiError && create.error.status === 400) && (
            <span style={{ flex: 1, minWidth: 200 }}>
              <ErrorNote message={(create.error as Error).message} />
            </span>
          )}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="navy" onClick={() => void submit()} disabled={!ready} title={ready ? undefined : 'Give the meeting a title.'}>
            {create.isPending ? 'Creating…' : continuing ? 'Create the continuation' : 'Create meeting'}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <legend style={{ fontSize: 12.5, fontWeight: 700, color: color.muted, marginBottom: 6 }}>What kind of meeting</legend>
          <label style={radio}>
            <input type="radio" name="mode" checked={mode === 'fresh'} onChange={() => setMode('fresh')} style={{ accentColor: color.blue }} />
            <span>
              <strong>Start fresh</strong>
              <span style={hint}>A new agenda, nothing carried over.</span>
            </span>
          </label>
          <label style={radio}>
            <input
              type="radio"
              name="mode"
              checked={mode === 'continue'}
              disabled={candidates.length === 0}
              onChange={() => {
                setMode('continue');
                if (!sourceId && candidates[0]) pickSource(candidates[0].id);
              }}
              style={{ accentColor: color.blue }}
            />
            <span>
              <strong>Continue a previous meeting</strong>
              <span style={hint}>
                {candidates.length === 0
                  ? 'There is no previous meeting yet.'
                  : 'Its action items, takeaways and open questions come along, ready to settle or put on the agenda.'}
              </span>
            </span>
          </label>
          {mode === 'continue' && candidates.length > 0 && (
            <div style={{ marginLeft: 26, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <select
                aria-label="Meeting to continue"
                value={source?.id ?? ''}
                onChange={(e) => pickSource(e.target.value)}
                style={{ ...selectStyle, width: '100%' }}
              >
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                    {c.scheduledAt ? ` · ${fullDate(c.scheduledAt)}` : ''}
                    {c.phase !== 'done' ? ` · ${c.phase === 'preparing' ? 'still preparing' : 'still in progress'}` : ''}
                  </option>
                ))}
              </select>
              {source && (
                <p style={{ fontSize: 12.5, color: color.ink, margin: 0 }}>
                  Carries over: <strong>{carryLine(source.carry)}</strong>
                </p>
              )}
            </div>
          )}
        </fieldset>

        <Field label="Title" error={errors.title}>
          <input
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setTouched(true);
            }}
            placeholder="Weekly team meeting"
            style={inputStyle}
            autoFocus
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Date" hint="Leave blank while it is being set up.">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Time">
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} style={inputStyle} disabled={!date} />
          </Field>
        </div>

        <Field label="Who is in the room" hint="You are always included. The summary goes to everyone ticked." error={errors.attendeeIds}>
          {people.isLoading ? (
            <p style={{ fontSize: 12.5, color: color.muted, margin: 0 }}>Loading people…</p>
          ) : people.error ? (
            <ErrorNote message={(people.error as Error).message} />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '6px 12px' }}>
              {(people.data?.people ?? []).map((p) => {
                const me = p.id === admin?.id;
                return (
                  <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: color.ink }}>
                    <input
                      type="checkbox"
                      checked={me || attendees.has(p.id)}
                      disabled={me}
                      onChange={() => toggle(p.id)}
                      style={{ width: 15, height: 15, accentColor: color.blue }}
                    />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name}
                      {me ? ' (you)' : ''}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </Field>
      </div>
    </Modal>
  );
}

const radio = { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5, color: color.ink, cursor: 'pointer' } as const;
const hint = { display: 'block', fontSize: 12.5, color: color.muted, marginTop: 2 } as const;
